<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10">
        <div class="card shadow-sm">
          <div class="card-body">
            <h2 class="mb-1">{{ titleText }}</h2>
  

            <div v-if="loading" class="py-5 text-center">
              <loading-spinner></loading-spinner>
            </div>

            <div v-else>
              <div v-if="error" class="alert alert-danger mb-3">
                {{ error }}
              </div>
              <div v-if="passwordRequired && !authorized" class="alert alert-warning">
                <div class="mb-2">This shared collection is password protected.</div>
                <div class="input-group">
                  <input
                    class="form-control"
                    type="password"
                    v-model="password"
                    placeholder="Enter password"
                    @keyup.enter="submitPassword"
                  />
                  <div class="input-group-append">
                    <button class="btn btn-primary" @click="submitPassword">Unlock</button>
                  </div>
                </div>
              </div>
              <div v-else>
              <p class="text-muted">
                Link access expires: {{ expiryText }}
              </p>
              <div class="mb-3">
                <button class="btn btn-sm btn-primary" :disabled="zipLoading || !clips.length" @click="downloadCollectionZip">
                  {{ zipButtonLabel }}
                </button>
              </div>
              <div v-if="!clips.length" class="alert alert-secondary">
                No clips are available in this collection.
              </div>
              <ul v-else class="list-group">
                <li
                  v-for="clip in clips"
                  :key="clip.id"
                  class="list-group-item"
                >
                  <div class="row align-items-center">
                    <div class="col-12 col-md-4 mb-2 mb-md-0" v-if="clip.file && clipDisplayUrl(clip.file)">
                      <video
                        v-if="clip.file.assetType === 'video'"
                        controls
                        muted
                        preload="metadata"
                        style="width: 100%; max-height: 180px;"
                      >
                        <source :src="clipDisplayUrl(clip.file)" type="video/mp4" />
                      </video>
                      <img
                        v-else
                        :src="clipDisplayUrl(clip.file)"
                        alt="Clip preview"
                        style="width: 100%; max-height: 180px; object-fit: cover;"
                      />
                    </div>
                    <div class="col-12 col-md-5">
                    <div class="font-weight-600">{{ clip.title || "Untitled clip" }}</div>
                    <small class="text-muted" v-if="clip.productionTitle">
                      {{ clip.productionTitle }}
                    </small>
                    <div class="small text-muted" v-if="clip.matchInfo">
                      <div v-if="clip.matchInfo.teams">{{ clip.matchInfo.teams }}</div>
                      <div v-if="clip.matchInfo.date">{{ formatMatchDate(clip.matchInfo.date) }}</div>
                      <div v-if="clip.matchInfo.venue">{{ clip.matchInfo.venue }}</div>
                      <div v-if="clip.matchInfo.format">Format: {{ clip.matchInfo.format }}</div>
                    </div>
                    <div class="small text-muted" v-if="clip.durationSeconds !== null">
                      Duration: {{ formatDuration(clip.durationSeconds) }}
                    </div>
                    </div>
                    <div class="col-12 col-md-3 text-md-right mt-2 mt-md-0">
                      <a
                        class="btn btn-sm btn-primary"
                        :href="clip.file.downloadUrl"
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </li>
              </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Firebase } from "@/firebase";
import { runZipJob } from "@/utils/zipJobs";

export default {
  name: "SharedCollection",
  data() {
    return {
      loading: true,
      error: "",
      collection: null,
      share: null,
      clips: [],
      password: "",
      passwordRequired: false,
      authorized: false,
      zipLoading: false,
      zipProgress: 0,
      zipStage: "",
    };
  },
  computed: {
    titleText() {
      return (this.collection && this.collection.name) || "Shared collection";
    },
    expiryText() {
      if (!this.share || !this.share.expiresAt) return "Unknown";
      return new Date(this.share.expiresAt).toLocaleString();
    },
    functionUrl() {
      const projectId = Firebase.app().options.projectId;
      return `https://europe-west2-${projectId}.cloudfunctions.net/publicCollectionShare`;
    },
    zipButtonLabel() {
      if (!this.zipLoading) return "Download all as ZIP";
      if (this.zipProgress > 0) return `Preparing ZIP (${this.zipProgress}%)`;
      if (this.zipStage) return `Preparing ZIP (${this.zipStage})`;
      return "Preparing ZIP";
    },
  },
  mounted() {
    this.fetchSharedCollection();
  },
  methods: {
    formatMatchDate(value) {
      if (!value) return "";
      let normalized = value;
      if (typeof value === "object") {
        if (typeof value._seconds === "number") {
          normalized = new Date(value._seconds * 1000);
        } else if (typeof value.seconds === "number") {
          normalized = new Date(value.seconds * 1000);
        }
      }
      const date = normalized instanceof Date ? normalized : new Date(normalized);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString();
    },
    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return "";
      const total = Math.max(0, Math.floor(seconds));
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    clipDisplayUrl(file) {
      if (!file) return "";
      return file.previewUrl || file.downloadUrl || "";
    },
    async fetchSharedCollection() {
      this.loading = true;
      this.error = "";
      try {
        const response = await fetch(this.functionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: this.$route.params.token || "",
            password: this.password || "",
          }),
        });
        const payload = await response.json();
        if (!response.ok) {
          if (payload && payload.passwordRequired) {
            this.passwordRequired = true;
            this.authorized = false;
          }
          throw new Error(payload.error || "Could not load shared collection");
        }
        this.collection = payload.collection || null;
        this.share = payload.share || null;
        this.clips = Array.isArray(payload.clips) ? payload.clips : [];
        this.passwordRequired = Boolean(payload.share && payload.share.passwordRequired);
        this.authorized = true;
      } catch (error) {
        this.error = error && error.message ? error.message : "Could not load shared collection";
      } finally {
        this.loading = false;
      }
    },
    submitPassword() {
      this.error = "";
      this.fetchSharedCollection();
    },
    async downloadCollectionZip() {
      if (this.zipLoading || !this.collection || !this.collection.id) return;
      const fileIds = (this.clips || [])
        .map((clip) => (clip && clip.file ? clip.file.id : ""))
        .filter(Boolean);
      if (!fileIds.length) {
        this.error = "No downloadable files found in this shared collection.";
        return;
      }

      const structure = {};
      (this.clips || []).forEach((clip) => {
        if (clip && clip.file && clip.file.id) {
          structure[clip.file.id] = clip.folderName || "";
        }
      });

      this.zipLoading = true;
      this.zipProgress = 0;
      this.zipStage = "queued";
      this.error = "";
      try {
        await runZipJob({
          createPayload: {
            collectionId: this.collection.id,
            fileIds,
            structure,
            zipFileName: `${this.collection.name || "Shared collection"} ${new Date().toISOString().slice(0, 10)}`,
            cacheKey: `shared-collection:${this.collection.id}:${fileIds.slice().sort().join(",")}`,
            shareToken: this.$route.params.token || "",
            password: this.password || "",
          },
          onCachedResult: async () => {
            const useCached = window.confirm(
              "A recent ZIP already exists for this shared collection.\n\nPress OK to download cached ZIP, or Cancel to rebuild."
            );
            return useCached ? "use-cache" : "rebuild";
          },
          onProgress: ({ progress, stage }) => {
            this.zipProgress = typeof progress === "number" ? Math.max(0, Math.min(100, Math.round(progress))) : 0;
            this.zipStage = stage || "";
          },
        });
      } catch (error) {
        this.error = error && error.message ? error.message : "Could not build ZIP.";
      } finally {
        this.zipLoading = false;
      }
    },
  },
};
</script>
